import { Component, inject, signal } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatButton } from '@angular/material/button';
import { MatFormField } from '@angular/material/form-field';
import { MatInput, MatLabel } from '@angular/material/input';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import type { SingleControlDialogData } from './single-control-dialog-data.interface';
import { ResolveTranslateAbleTextPipe } from '@pct-ng/i18n';
import { ControlErrorComponent } from '@pct-ng/form';
import { isFunction, isNil } from '@pct-ts/typeguard';
import { finalize } from 'rxjs/operators';
import { hasErrorCode } from '../functions/has-error-code';
import { ensureItemsArray } from '@pct-ts/common';
import { whitespaceValidator } from '../validators/whitespace.validator';

@Component({
  selector: 'cappus-single-control-dialog',
  imports: [
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButton,
    MatFormField,
    MatInput,
    FormsModule,
    MatDialogClose,
    MatLabel,
    ReactiveFormsModule,
    ResolveTranslateAbleTextPipe,
    ControlErrorComponent,
  ],
  templateUrl: './single-control-dialog.component.html',
})
export class SingleControlDialogComponent {
  readonly #dialogRef = inject(MatDialogRef<SingleControlDialogComponent, string | null>);
  protected readonly data: SingleControlDialogData = inject(MAT_DIALOG_DATA);
  protected readonly control = new FormControl<string>(this.data.inputDefaultValue ?? '', {
    validators: [...(this.data.validators ?? []), whitespaceValidator()],
    nonNullable: true,
  });
  protected readonly defaultSubmitLabelTextKey = 'COMMON.SAVE';
  protected readonly defaultCancelLabelTextKey = 'COMMON.CANCEL';
  protected submitted = false;
  protected loading = signal(false);

  onSubmit(): void {
    this.submitted = true;
    this.control.markAsTouched();
    if (this.control.invalid) {
      return;
    }
    const controlValue = this.control.value;
    if (!isFunction(this.data.onSubmit)) {
      this.#dialogRef.close(controlValue);
      return;
    }
    this.loading.set(true);
    this.control.disable();
    this.data
      .onSubmit(controlValue)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: () => this.#dialogRef.close(controlValue),
        error: error => {
          this.control.enable();
          if (isNil(this.data.errorCodes)) {
            return;
          }
          const errorCodes = ensureItemsArray(this.data.errorCodes);
          for (const errorCode of errorCodes) {
            if (hasErrorCode(error, errorCode)) {
              this.control.setErrors({
                [errorCode]: true,
              });
              return;
            }
          }
        },
      });
  }
}
